/**
 * MapBottomDrawer - Domain Component
 * 
 * Bottom drawer for map search results with 3 snap states:
 * - Minimized: Shows result count only
 * - Partial (Peek): Shows result count + first listing card
 * - Full: Full screen list view
 * 
 * This is a domain-specific component for the map search experience.
 * It composes the atomic Drawer component with map search business logic.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, DrawerSnapPoint } from 'components/atomic/organisms/drawer';
import { DrawerHeader } from 'components/atomic/molecules/drawer-header';
import type { ListingCardData } from 'components/listing-card/listing-card';
import { ListingCardDataWithCompliance } from 'utils/listing-card/listing-card-transformers';
import { buildClassName } from 'utils/build-class-name';
import styles from './map-bottom-drawer.module.scss';
import { formatNumberWithSuffix } from 'utils/format-number-with-suffix';
import ListingCard from 'components/listing-card/listing-card';
import ListingCardV2 from 'components/listing-card/listing-card-v2';
import { AllowedExperimentKey } from '@tenants/types';
import { useExperimentVariation, useExperimentTracking } from 'ui/experimental-section/hooks';
import { useTenantId } from 'hooks/use-tenant-id';
import Cookies from 'js-cookie';
import { PROPERTY_DRAWER_AUTO_COLLAPSE_COOKIE_NAME } from 'constants/cookies';

export interface MapBottomDrawerProps {
  /** Listings to display */
  listings: ListingCardData[];
  
  /** V2 listings with compliance (optional) */
  listingsWithCompliance?: ListingCardDataWithCompliance[];
  
  /** Total count of listings */
  totalCount: number;
  
  /** Title for the drawer header*/
  title?: string;
  
  /** Whether to use v2 listing card with compliance */
  useListingCardWithCompliance?: boolean;
  
  /** Callback when listing card is clicked */
  onListingCardClick?: (listing: ListingCardData) => void;
  
  /** Callback when more items need to be loaded (for lazy loading) */
  onLoadMoreItems?: (startIndex: number, stopIndex: number) => void;
  
  /** Callback when drawer state changes */
  onStateChange?: (state: DrawerSnapPoint) => void;
  
  /** Initial snap state when the drawer mounts */
  initialState?: DrawerSnapPoint;
  
  /** Additional className */
  className?: string;
  
  /** Custom z-index for the drawer */
  zIndex?: number;
  
  /** Full height in px. Defaults to window.innerHeight. Overridden by topOffset. */
  fullHeight?: number;
  
  /** Distance from viewport top where the drawer stops in Full state. */
  topOffset?: number;
}

/**
 * MapBottomDrawer Component
 * 
 * Domain component that provides a mobile-optimized bottom drawer
 * for browsing map search results with a "peek" state.
 */
export function MapBottomDrawer({
  listings,
  listingsWithCompliance,
  totalCount,
  title,
  useListingCardWithCompliance,
  onListingCardClick,
  onStateChange,
  initialState = DrawerSnapPoint.Partial,
  className,
  zIndex = 10,
  fullHeight: fullHeightProp,
  topOffset,
}: MapBottomDrawerProps) {
  const [clientHeight, setClientHeight] = useState<number | null>(null);

  useEffect(() => {
    setClientHeight(window.innerHeight);
  }, []);

  // ─── EXPERIMENT: property-drawer-auto-collapse
  // Variant A - Drawer is partially opened
  // Variant B - Drawer is partially opened initially, then collapses with animation.

  const AUTO_COLLAPSE_DELAY_MS = 2000;
  const tenantId = useTenantId();
  const { showVariation, variationId } =
    useExperimentVariation(AllowedExperimentKey.PROPERTY_DRAWER_AUTO_COLLAPSE);
  useExperimentTracking(AllowedExperimentKey.PROPERTY_DRAWER_AUTO_COLLAPSE, variationId, tenantId);

  // Check if variant B is active and the animation hasn't played yet. After animation is played, cookie to indicate this is saved.
  const shouldAutoCollapse = showVariation
    && !Cookies.get(PROPERTY_DRAWER_AUTO_COLLAPSE_COOKIE_NAME);

  const initialDrawerState = shouldAutoCollapse
    ? DrawerSnapPoint.Partial
    : showVariation
      ? DrawerSnapPoint.Minimized
      : initialState;

  const [drawerState, setDrawerState] = useState<DrawerSnapPoint>(initialDrawerState);

  useEffect(() => {
    if (!shouldAutoCollapse) return;

    const timerId = setTimeout(() => {
      setDrawerState(DrawerSnapPoint.Minimized);
      Cookies.set(PROPERTY_DRAWER_AUTO_COLLAPSE_COOKIE_NAME, '1', { path: '/' });
    }, AUTO_COLLAPSE_DELAY_MS);

    return () => clearTimeout(timerId);
  }, [shouldAutoCollapse]);
  // ─── END EXPERIMENT ───
  
  const MAX_VISIBLE_LISTINGS = 20;
  const visibleListings = useMemo(() => listings.slice(0, MAX_VISIBLE_LISTINGS), [listings]);
  const visibleListingsWithCompliance = useMemo(
    () => listingsWithCompliance?.slice(0, MAX_VISIBLE_LISTINGS),
    [listingsWithCompliance]
  );

  const screenHeight = clientHeight ?? 800;
  const minimizedHeight = 48;
  const partialHeight = Math.round(screenHeight * 0.5);
  const fullHeight = topOffset != null
    ? screenHeight - topOffset
    : fullHeightProp != null ? fullHeightProp : screenHeight;
  
  const handleStateChange = useCallback((newState: DrawerSnapPoint) => {
    setDrawerState(newState);
    onStateChange?.(newState);
  }, [onStateChange]);
  
  const handleBackdropClick = useCallback(() => {
    setDrawerState(DrawerSnapPoint.Minimized);
    onStateChange?.(DrawerSnapPoint.Minimized);
  }, [onStateChange]);
  
  const listingCountText = title || `${formatNumberWithSuffix(totalCount, false)} Homes in Map Area`;

  return (
    <Drawer
      currentState={drawerState}
      onStateChange={handleStateChange}
      minimizedHeight={minimizedHeight}
      partialHeight={partialHeight}
      fullHeight={fullHeight}
      className={buildClassName(styles['map-bottom-drawer'], className)}
      enableGestures
      showHandle
      partialDragRatio={0}
      onBackdropClick={handleBackdropClick}
      expandOnScroll
      zIndex={zIndex}
      header={
        <DrawerHeader className={styles['compact-header']}>
          <div className={styles['drawer-header-content']}>
            <div className={styles['header-text-section']}>
              <h3 className={styles['listing-count']}>{listingCountText}</h3>
            </div>
          </div>
        </DrawerHeader>
      }
    >
      <div className={styles['drawer-content']}>
        {visibleListings.length === 0 ? (
          <div className={styles['empty-state']}>
            <p>No listings found in this area</p>
          </div>
        ) : (
          <div className={styles['listing-cards']}>
            {useListingCardWithCompliance && visibleListingsWithCompliance ? (
              visibleListingsWithCompliance.map((complianceListing, index) => (
                <div key={complianceListing.id} className={styles['listing-card-wrapper']}>
                  <ListingCardV2
                    listing={complianceListing}
                    onListingCardClick={() => onListingCardClick?.(visibleListings[index])}
                  />
                </div>
              ))
            ) : (
              visibleListings.map(listing => (
                <div key={listing.id} className={styles['listing-card-wrapper']}>
                  <ListingCard
                    listing={listing}
                    onListingCardClick={() => onListingCardClick?.(listing)}
                  />
                </div>
              ))
            )}
            
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default MapBottomDrawer;
